/* TranslatedPage — wraps a public page and auto-translates user-visible text
 * nodes after each render, when the active language is not English.
 *
 * How it works:
 * 1. We mount a wrapper div and walk its descendant text nodes on every render.
 * 2. Each non-empty, non-numeric text node has its original English content
 *    stored once in a WeakMap (so we always retranslate from English, not from
 *    the previous translation).
 * 3. We batch all collected English strings, send them to /api/translate via
 *    the global LanguageContext (which dedupes + caches), then walk again and
 *    swap each node's text to the translated version.
 * 4. Elements marked with data-no-translate="true" are skipped (safe zone for
 *    Stripe buttons, donation amount selectors, country codes, etc.).
 * 5. <input>/<textarea> placeholders + aria-labels are also translated.
 *
 * This means we DON'T need to rewrite every component. Just wrap the public
 * pages in <TranslatedPage>...</TranslatedPage> and they translate.
 */
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE',
  'INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'BUTTON', 'IMG', 'SVG', 'PATH']);
// Translate text inside these even though they look like form widgets:
const FORCE_TRANSLATE_TAGS = new Set(['LABEL']);

// English source per node, so re-translation is from English not from previous
// translation. Keyed by the text node itself.
const sourceMap = new WeakMap();
// Tracks which nodes are currently displaying the correct translation for the
// active language so MutationObserver re-scans skip them.
const translatedAt = new WeakMap(); // node -> lang code currently applied

const isTranslatable = (text) => {
  if (!text) return false;
  const s = text.trim();
  if (!s) return false;
  // Skip pure numbers, currency, dates, symbols
  if (/^[\d\s.,\-+%$€£¥/x:#@]+$/.test(s)) return false;
  // Skip strings with no letters at all (arrows, dividers, etc.)
  if (!/[A-Za-zÀ-ÿ]/.test(s)) return false;
  // Skip URLs
  if (/^https?:\/\//i.test(s) || /^mailto:/i.test(s)) return false;
  return true;
};

const isInsideNoTranslate = (node) => {
  let el = node.nodeType === 1 ? node : node.parentElement;
  while (el) {
    if (el.dataset && el.dataset.noTranslate === 'true') return true;
    el = el.parentElement;
  }
  return false;
};

const isInsideSkipTag = (node) => {
  let el = node.parentElement;
  while (el) {
    if (FORCE_TRANSLATE_TAGS.has(el.tagName)) {
      // Within label — allowed even if inside a form element ancestor
      return false;
    }
    if (SKIP_TAGS.has(el.tagName)) return true;
    el = el.parentElement;
  }
  return false;
};

const collectStrings = (root) => {
  const items = []; // { node, original }
  const placeholderItems = []; // { el, attr }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (isInsideNoTranslate(n)) return NodeFilter.FILTER_REJECT;
      if (isInsideSkipTag(n)) return NodeFilter.FILTER_REJECT;
      if (!isTranslatable(n.nodeValue)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n = walker.nextNode();
  while (n) {
    // Cache the original English text the first time we see this node
    if (!sourceMap.has(n)) sourceMap.set(n, n.nodeValue);
    items.push({ node: n, original: sourceMap.get(n) });
    n = walker.nextNode();
  }

  // Translate placeholders + aria-labels on inputs/textareas
  root.querySelectorAll('input[placeholder], textarea[placeholder]').forEach((el) => {
    if (isInsideNoTranslate(el)) return;
    const v = el.getAttribute('placeholder');
    if (!isTranslatable(v)) return;
    if (!el.__nhf_orig_placeholder) el.__nhf_orig_placeholder = v;
    placeholderItems.push({ el, attr: 'placeholder', original: el.__nhf_orig_placeholder });
  });
  root.querySelectorAll('[aria-label]').forEach((el) => {
    if (isInsideNoTranslate(el)) return;
    const v = el.getAttribute('aria-label');
    if (!isTranslatable(v)) return;
    if (!el.__nhf_orig_aria) el.__nhf_orig_aria = v;
    placeholderItems.push({ el, attr: 'aria-label', original: el.__nhf_orig_aria });
  });

  return { items, placeholderItems };
};

const TranslatedPage = ({ children, context = 'page' }) => {
  const wrapperRef = useRef(null);
  const { lang, dictionaries, setDictionaries, persistDictionary, trackInflight } = useLanguage();
  const inflightRef = useRef(false);
  const rescanTimerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const root = wrapperRef.current;

    // Apply a dictionary to the DOM in a single rAF so all swaps land
    // together (avoids the piecemeal flicker users were noticing).
    const apply = (dict) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const { items, placeholderItems } = collectStrings(root);
        for (const { node, original } of items) {
          if (lang === 'en') {
            if (node.nodeValue !== original) node.nodeValue = original;
            translatedAt.delete(node);
            continue;
          }
          // Already showing the right translation for this language? Skip.
          if (translatedAt.get(node) === lang) continue;
          const t = dict[original];
          if (t === undefined) continue;
          // SAFETY: never replace a non-empty source with an empty/whitespace
          // translation — that's what made data "disappear". Keep the original.
          if (typeof t !== 'string' || (original.trim() && !t.trim())) continue;
          if (node.nodeValue !== t) node.nodeValue = t;
          translatedAt.set(node, lang);
        }
        for (const { el, attr, original } of placeholderItems) {
          if (lang === 'en') {
            if (el.getAttribute(attr) !== original) el.setAttribute(attr, original);
            continue;
          }
          const t = dict[original];
          if (typeof t !== 'string' || (original.trim() && !t.trim())) continue;
          if (el.getAttribute(attr) === t) continue;
          el.setAttribute(attr, t);
        }
      });
    };

    const fetchAndApply = async () => {
      if (lang === 'en') { apply({}); return; }
      const dict = dictionaries[lang] || {};
      // Apply already-cached translations immediately to avoid flicker
      apply(dict);

      // Find missing translations and fetch them in one call
      const { items, placeholderItems } = collectStrings(root);
      const missing = new Set();
      [...items, ...placeholderItems].forEach((it) => {
        const v = it.original ?? sourceMap.get(it.node);
        if (v && dict[v] === undefined) missing.add(v);
      });
      if (missing.size === 0 || inflightRef.current) return;

      inflightRef.current = true;
      if (trackInflight) trackInflight(1);
      const texts = Array.from(missing);
      try {
        const res = await fetch(`${BACKEND_URL}/api/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ texts, target_lang: lang, context }),
        });
        const data = await res.json();
        const map = data?.translations || {};
        // Merge into the global dictionary so other pages benefit + reloads stay instant
        if (setDictionaries) {
          setDictionaries((prev) => {
            const merged = { ...(prev[lang] || {}), ...map };
            if (persistDictionary) persistDictionary(lang, merged);
            // Apply the merged dictionary immediately on this page
            apply(merged);
            return { ...prev, [lang]: merged };
          });
        } else {
          apply({ ...dict, ...map });
        }
      } catch {
        /* silent — page stays English */
      } finally {
        inflightRef.current = false;
        if (trackInflight) trackInflight(-1);
      }
    };

    // Initial pass
    fetchAndApply();

    // MutationObserver — catches late-arriving API content (events, updates,
    // program cards, shop items, etc.). Debounced so 5 async loads = 1 fetch.
    const observer = new MutationObserver(() => {
      if (rescanTimerRef.current) clearTimeout(rescanTimerRef.current);
      rescanTimerRef.current = setTimeout(fetchAndApply, 400);
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: false, // we mutate text ourselves; don't re-trigger on our own writes
    });

    return () => {
      observer.disconnect();
      if (rescanTimerRef.current) clearTimeout(rescanTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [lang, dictionaries, context, setDictionaries, persistDictionary, trackInflight]);

  return (
    <div ref={wrapperRef} data-translated-page={lang} data-translate-context={context}>
      {children}
    </div>
  );
};

export default TranslatedPage;
